import { useCallback, useMemo } from "react";

import type { ColumnsType } from "@similarweb/pro-logic-components";

import useLatestValue from "app/custom-hooks/useLatestValue";
import type { ContactsTableColumnsExternalProps } from "app/pages/sales-intelligence/sub-modules/contacts-search/configuration/columns/createContactsResultsTableColumns";
import type { ContactSearchEntry, CRMEntity } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

const useCreateContactsResultsTableColumns = (
    { totalCount, onExportItemToCRM, onExportItemToExel, onRefetchData }: Partial<ContactsTableColumnsExternalProps>,
    createColumnsConfigFactory: (props: ContactsTableColumnsExternalProps) => ColumnsType<ContactSearchEntry>,
    availableColumns?: ReadonlyArray<keyof ContactSearchEntry>,
): ColumnsType<ContactSearchEntry> => {
    const exportItemToExelRef = useLatestValue(onExportItemToExel);
    const exportItemToCRMRef = useLatestValue(onExportItemToCRM);
    const refetchDataRef = useLatestValue(onRefetchData);

    const handleExportItemToExel = useCallback((contact: ContactSearchEntry) => {
        exportItemToExelRef.current?.(contact);
    }, []);

    const handleExportItemToCRM = useCallback(
        (contact: ContactSearchEntry, destination: Integration, as: CRMEntity) => {
            exportItemToCRMRef.current?.(contact, destination, as);
        },
        [],
    );

    const handleRefetchData = useCallback(async () => {
        await refetchDataRef.current?.();
    }, []);

    return useMemo((): ColumnsType<ContactSearchEntry> => {
        const columns = createColumnsConfigFactory({
            onExportItemToExel: handleExportItemToExel,
            onExportItemToCRM: handleExportItemToCRM,
            totalCount,
            onRefetchData: handleRefetchData,
        });

        if (availableColumns) {
            return columns.filter((column) => availableColumns.includes(column.dataIndex as keyof ContactSearchEntry));
        }

        return columns;
    }, [
        availableColumns,
        totalCount,
        handleExportItemToExel,
        handleExportItemToCRM,
        handleRefetchData,
        createColumnsConfigFactory,
    ]);
};

export default useCreateContactsResultsTableColumns;
